import { useEffect, useMemo, useRef, useState } from 'react'
import { useIsMutating, type UseQueryResult } from '@tanstack/react-query'
import { isLosslessNumber } from 'lossless-json'
import { dealModelKeys, useDealModelCurrent, useSaveDealsV2PropertyOverrides, type DealsV2PropertyOverrides } from '../../../api/dealModels'
import type { DealsV2PropertyModelChoice } from '../../../api/financial'
import type { DealModelReturnTargets } from '../../../api/dealModelLayout'
import { normalizeSettingsDecimal, shiftSettingsDecimal } from '../../settings/settingsFieldPresentation'
import { compareDecimal } from './dealLayout'

export type FinancialTargetMode = 'modelDefault' | 'margin' | 'profit'
export interface FinancialTargetDraft { mode: 'margin' | 'profit'; value: string }
function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) && !isLosslessNumber(value) ? value as Record<string, unknown> : null
}

/** Source IDs identify the economic bindings; keys and override keys may differ. */
export function financialReturnTargetBindings(source: unknown): DealModelReturnTargets | null {
  const document = objectValue(source)
  const declared = objectValue(document?.returnTargets)
  if (!declared || !Array.isArray(document?.inputs)) return null
  const inputs = document.inputs.map(objectValue)
  const input = (id: unknown) => typeof id === 'string' ? inputs.find(value => value?.id === id) : null
  const mode = input(declared.modeInputId)
  const candidate = input(declared.candidateInputId)
  const modeKey = objectValue(mode?.resolution)?.propertyOverrideKey
  if (typeof mode?.id !== 'string' || typeof mode.key !== 'string' || typeof modeKey !== 'string' ||
    typeof candidate?.id !== 'string' || typeof candidate.key !== 'string') return null
  const metric = (value: unknown) => {
    const ref = objectValue(value)
    const bound = input(ref?.inputId)
    const overrideKey = objectValue(bound?.resolution)?.propertyOverrideKey
    return typeof bound?.id === 'string' && typeof bound.key === 'string' && typeof overrideKey === 'string' && typeof ref?.outputId === 'string'
      ? { inputId: bound.id, inputKey: bound.key, overrideKey, outputId: ref.outputId } : undefined
  }
  const margin = metric(declared.margin)
  const profit = metric(declared.profit)
  if ((declared.margin && !margin) || (declared.profit && !profit) || (!margin && !profit)) return null
  const candidateOverrideKey = objectValue(candidate.resolution)?.propertyOverrideKey
  return { modeInputId: mode.id, modeInputKey: mode.key, modeOverrideKey: modeKey,
    candidateInputId: candidate.id, candidateInputKey: candidate.key,
    candidateOverrideKey: typeof candidateOverrideKey === 'string' ? candidateOverrideKey : null,
    ...(margin ? { margin } : {}), ...(profit ? { profit } : {}) }
}

export function sameFinancialTargetBindings(a: DealModelReturnTargets, b: DealModelReturnTargets): boolean {
  const metric = (name: 'margin' | 'profit') => !a[name] && !b[name] || Boolean(a[name] && b[name] &&
    a[name]!.inputId === b[name]!.inputId && a[name]!.inputKey === b[name]!.inputKey &&
    a[name]!.overrideKey === b[name]!.overrideKey && a[name]!.outputId === b[name]!.outputId)
  return a.modeInputId === b.modeInputId && a.modeInputKey === b.modeInputKey && a.modeOverrideKey === b.modeOverrideKey &&
    a.candidateInputId === b.candidateInputId && a.candidateInputKey === b.candidateInputKey && a.candidateOverrideKey === b.candidateOverrideKey && metric('margin') && metric('profit')
}

function targetKeys(bindings: DealModelReturnTargets): Set<string> {
  return new Set([bindings.modeOverrideKey, ...[bindings.margin, bindings.profit].flatMap(metric => metric ? [metric.overrideKey] : [])])
}
type Overrides = Pick<UseQueryResult<DealsV2PropertyOverrides, Error>, 'data' | 'isFetching'>
/** Exact editable units, validated against this model's declared input domain. */
export function targetOverride(source: unknown, draft: FinancialTargetDraft): { key: string; value: string } {
  const bindings = financialReturnTargetBindings(source)
  const binding = bindings?.[draft.mode]
  if (!binding) throw new Error('This model does not declare this target.')
  const normalized = normalizeSettingsDecimal(draft.value)
  if (normalized === null) throw new Error('Enter a valid target.')
  if ((normalized.split('.')[1]?.length ?? 0) > 2) throw new Error(`Use at most two decimal places for ${draft.mode === 'margin' ? 'margin' : 'profit'}.`)
  const value = draft.mode === 'margin' ? shiftSettingsDecimal(normalized, -2)! : normalized
  // Decimal128(30,12) is the server representation, not a separate business cap.
  if ((value.split('.')[0]?.replace(/^-?0*/, '').length ?? 0) > 18) throw new Error('The target cannot be represented by the calculation engine.')
  const inputs = objectValue(source)?.inputs
  const input = Array.isArray(inputs) ? inputs.map(objectValue).find(item => item?.id === binding.inputId) : null
  const semantic = objectValue(input?.semanticType)
  if (!semantic) throw new Error('This model does not declare this target.')
  for (const [bound, minimum] of [['minimum', true], ['maximum', false]] as const) {
    if (semantic[bound] == null) continue
    const comparison = compareDecimal(value, String(semantic[bound]))
    if (comparison === null || (minimum ? comparison < 0 : comparison > 0)) throw new Error('The target is outside this model’s allowed range.')
  }
  if ((value.split('.')[1]?.length ?? 0) > 12) throw new Error('The target has more precision than the calculation engine supports.')
  return { key: binding.overrideKey, value }
}

/** Replace the active driver; preserve every unrelated fixed assumption. */
export function withFinancialTarget(saved: Record<string, unknown>, source: unknown, draft: FinancialTargetDraft | null): Record<string, unknown> {
  const bindings = financialReturnTargetBindings(source)
  if (!bindings) throw new Error('This model does not declare return targets.')
  const keys = targetKeys(bindings)
  const otherInputs = Object.fromEntries(Object.entries(saved).filter(([key]) => !keys.has(key)))
  if (!draft) return otherInputs
  if (bindings.candidateOverrideKey && Object.hasOwn(saved, bindings.candidateOverrideKey)) throw new Error('The purchase input is fixed. Reset it in Adjust before applying a return target.')
  const target = targetOverride(source, draft)
  return { ...otherInputs, [bindings.modeOverrideKey]: draft.mode, [target.key]: target.value }
}

/** Target edits are drafts only. The server computes and commits on Apply;
 * the shared mutation then invalidates cards, model selection and details. */
export function useFinancialTarget({ model, overrides, propertyId, profileId, enabled }: {
  model: DealsV2PropertyModelChoice | null; overrides: Overrides; propertyId: string; profileId: string; enabled: boolean
}) {
  const capability = model?.layout?.returnTargets
  const supported = Boolean(capability)
  const source = useDealModelCurrent(model?.logicalModelId ?? '', supported)
  const save = useSaveDealsV2PropertyOverrides(propertyId)
  const mutations = useIsMutating({ mutationKey: dealModelKeys.savePropertyOverrides(propertyId) })
  const scope = `${propertyId}:${model?.logicalModelId ?? ''}`
  const [editing, setEditing] = useState<{ scope: string; value: FinancialTargetDraft } | null>(null)
  const [actionError, setActionError] = useState<{ scope: string; message: string } | null>(null)
  const draft = editing?.scope === scope ? editing.value : null
  const latestScope = useRef(scope)
  latestScope.current = scope
  const draftRef = useRef(draft)
  draftRef.current = draft
  const inFlight = useRef<{ scope: string } | null>(null)
  const saved = overrides.data?.profileId === profileId && overrides.data.propertyId === propertyId ? overrides.data : null
  const modeValue = capability ? saved?.values[capability.modeOverrideKey] : null
  const savedMode: FinancialTargetMode = (modeValue === 'margin' || modeValue === 'profit') && capability?.[modeValue] ? modeValue : 'modelDefault'
  const savedBinding = savedMode === 'modelDefault' ? null : capability?.[savedMode]
  const savedRaw = savedBinding ? saved?.values[savedBinding.overrideKey] : null
  const savedValue = savedRaw == null ? null : normalizeSettingsDecimal(String(savedRaw))
  const savedTarget: FinancialTargetDraft | null = savedMode !== 'modelDefault' && savedValue !== null
    ? { mode: savedMode, value: savedMode === 'margin' ? shiftSettingsDecimal(savedValue, 2)! : savedValue }
    : null
  const currentSource = source.data
  const document = currentSource?.sourceDocument
  const sourceBindings = useMemo(() => financialReturnTargetBindings(document), [document])
  const setDraft = (next: FinancialTargetDraft | null) => {
    if (inFlight.current?.scope === scope) return
    save.reset()
    setActionError(null)
    draftRef.current = next
    setEditing(next ? { scope, value: next } : null)
  }
  useEffect(() => { setEditing(null); setActionError(null); save.reset() }, [scope])

  const validation = useMemo(() => {
    if (!saved || !draft || !document) return null
    try { withFinancialTarget(saved.values, document, draft); return null }
    catch (error) { return error instanceof Error ? error.message : String(error) }
  }, [saved, document, draft])
  const prerequisite = !enabled ? 'Wait for the current model and saved inputs before changing targets.'
    : !saved || overrides.isFetching || !currentSource ? 'Loading the current model and your values.'
      : !capability || !sourceBindings || !sameFinancialTargetBindings(capability, sourceBindings) || currentSource.sourceSha256 !== saved.sourceSha256 || (saved.storedSourceSha256 && saved.storedSourceSha256 !== saved.sourceSha256) ? 'The model changed. Reload or recalculate before changing targets.'
        : capability.candidateOverrideKey && Object.hasOwn(saved.values, capability.candidateOverrideKey) ? 'The purchase input is fixed. Reset it in Adjust before applying a return target.'
          : null
  const saving = save.isPending || mutations > 0
  // Apply invalidates the model/profile reads. Their normal refresh can disable
  // editing briefly; it is pending state, not a failed target application.
  const refreshing = !enabled || overrides.isFetching || source.isFetching
  const ready = supported && !refreshing && prerequisite === null
  const validationError = validation ?? source.error?.message
    ?? (!saving && !refreshing && prerequisite && !prerequisite.startsWith('Loading') ? prerequisite : null)
  const error = validationError ?? (actionError?.scope === scope ? actionError.message : null) ?? save.error?.message ?? null
  const busy = supported && !saving && (refreshing || prerequisite?.startsWith('Loading') === true)
  const dirty = draft !== null
  const apply = async () => {
    if (!saved || !draft || !ready || busy || validationError || mutations > 0 || inFlight.current?.scope === scope) return
    const sent = draft
    const sentScope = scope
    const request = { scope: sentScope }
    inFlight.current = request
    setActionError(null)
    try {
      await save.mutateAsync({ profileId, propertyId, expectedRevision: saved.revision, expectedSourceSha256: saved.sourceSha256, values: withFinancialTarget(saved.values, document, sent) })
      // Success acknowledges the persisted request. Numerical displays remain
      // canonical query data; Reset may legitimately relink to a baseline profile.
      if (latestScope.current === sentScope && draftRef.current === sent) {
        draftRef.current = null
        setEditing(null)
      }
    } catch (error) {
      if (latestScope.current === sentScope) setActionError({ scope: sentScope, message: error instanceof Error ? error.message : 'The target could not be saved.' })
    }
    finally { if (inFlight.current === request) inFlight.current = null }
  }
  const reset = async () => {
    if (mutations > 0 || inFlight.current?.scope === scope) return
    if (!savedTarget) { setDraft(null); return }
    if (!saved || overrides.isFetching || !currentSource || currentSource.sourceSha256 !== saved.sourceSha256) return
    const sent = draft
    const sentScope = scope
    const request = { scope: sentScope }
    inFlight.current = request
    setActionError(null)
    try {
      await save.mutateAsync({ profileId, propertyId, expectedRevision: saved.revision, expectedSourceSha256: saved.sourceSha256, values: withFinancialTarget(saved.values, document, null) })
      if (latestScope.current === sentScope && draftRef.current === sent) {
        draftRef.current = null
        setEditing(null)
      }
    } catch (error) {
      if (latestScope.current === sentScope) setActionError({ scope: sentScope, message: error instanceof Error ? error.message : 'The target could not be reset.' })
    }
    finally { if (inFlight.current === request) inFlight.current = null }
  }
  const labels = {
    margin: model?.presentation.outputs.find(output => output.outputId === capability?.margin?.outputId)?.label ?? 'Margin',
    profit: model?.presentation.outputs.find(output => output.outputId === capability?.profit?.outputId)?.label ?? 'Profit',
  }
  return { supported, capability, labels, mode: draft?.mode ?? savedMode, draft, setDraft, savedTarget, busy, error, validationError, apply, reset, saving, dirty, ready }
}
